import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { falModelOptions } from "@/lib/playground";

type FalModelPickerProps = {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
};

export const FalModelPicker = ({
  value,
  onChange,
  disabled,
}: FalModelPickerProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const needle = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      needle
        ? falModelOptions.filter(
            (option) =>
              option.id.toLowerCase().includes(needle) ||
              option.label.toLowerCase().includes(needle)
          )
        : falModelOptions,
    [needle]
  );

  const customCandidate =
    needle.length > 0 && !falModelOptions.some((option) => option.id === query.trim());

  const select = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  const rowClass = (selected: boolean) =>
    cn(
      "block w-full border-l-2 py-1.5 pl-3 pr-2 text-left text-sm transition-all hover:border-primary hover:bg-secondary hover:pl-4",
      selected ? "border-primary bg-secondary" : "border-transparent"
    );

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left",
          "font-mono text-xs text-foreground transition-colors hover:bg-black/[0.04]",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
      >
        <span className="truncate">{value || "pick a model"}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-20 border border-foreground bg-popover">
          <div className="border-b p-2">
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search or type any fal-ai model id…"
              className="h-8 bg-transparent font-mono text-xs"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto p-1">
            {filtered.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => select(option.id)}
                  className={rowClass(option.id === value)}
                >
                  <span className="block truncate text-sm">{option.label}</span>
                  <span className="block truncate font-mono text-[11px] text-subtle">
                    {option.id}
                  </span>
                </button>
              </li>
            ))}
            {customCandidate ? (
              <li>
                <button
                  type="button"
                  onClick={() => select(query.trim())}
                  className={rowClass(false)}
                >
                  <span className="block truncate text-sm">
                    Use custom model id
                  </span>
                  <span className="block truncate font-mono text-[11px] text-primary">
                    {query.trim()}
                  </span>
                </button>
              </li>
            ) : null}
            {!filtered.length && !customCandidate ? (
              <li className="px-3 py-2 font-mono text-xs text-muted-foreground">
                Type a full fal-ai model id to use it.
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
